import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, Mail, Phone, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { educationAPI } from '../api/axiosClient';
import { ParentDetails, ParentRelationType } from '../api/types';
import toast from 'react-hot-toast';

interface ParentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string;
}

const ParentDetailModal: React.FC<ParentDetailModalProps> = ({
  isOpen,
  onClose,
  parentId
}) => {
  const [parent, setParent] = useState<ParentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadParentDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await educationAPI.getParentDetails(parentId);
      if (response.success && response.data) {
        setParent(response.data);
      }
    } catch (error: any) {
      console.error('Error loading parent details:', error);
      toast.error('Veli detayları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [parentId]);

  useEffect(() => {
    if (isOpen && parentId) {
      loadParentDetails();
    }
  }, [isOpen, parentId, loadParentDetails]);

  const getRelationTypeName = (relationType: ParentRelationType): string => {
    switch (relationType) {
      case ParentRelationType.Mother:
        return 'Anne';
      case ParentRelationType.Father:
        return 'Baba';
      case ParentRelationType.Guardian:
        return 'Vasi';
      case ParentRelationType.Other:
        return 'Diğer';
      default:
        return 'Bilinmiyor';
    }
  };

  const getRelationTypeColor = (relationType: ParentRelationType): string => {
    switch (relationType) {
      case ParentRelationType.Mother:
        return 'bg-pink-100 text-pink-800';
      case ParentRelationType.Father:
        return 'bg-blue-100 text-blue-800';
      case ParentRelationType.Guardian:
        return 'bg-green-100 text-green-800';
      case ParentRelationType.Other:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-6 h-6 mr-2 text-[#9B51E0]" />
              Veli Detayları
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
            </div>
          ) : parent ? (
            <div className="space-y-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Kişisel Bilgiler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Ad Soyad</label>
                        <p className="text-lg font-semibold text-gray-900">
                          {parent.firstName} {parent.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Veli İlişkisi</label>
                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRelationTypeColor(
                              parent.relationType as unknown as ParentRelationType
                            )}`}
                          >
                            {getRelationTypeName(parent.relationType as unknown as ParentRelationType)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Kimlik Numarası</label>
                        <p className="text-gray-900 font-mono">{parent.identityNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Meslek</label>
                        <p className="text-gray-900">{parent.occupation}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Phone className="w-5 h-5 mr-2 text-green-600" />
                    İletişim Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">E-posta</label>
                        <p className="text-gray-900">{parent.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-500">Telefon</label>
                        <p className="text-gray-900">
                          {parent.phoneCode} {parent.phoneNumber}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Students Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Users className="w-5 h-5 mr-2 text-purple-600" />
                    Öğrenciler ({parent.students?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {parent.students && parent.students.length > 0 ? (
                    <div className="space-y-2">
                      {parent.students.map((student: any, index: number) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-[#9B51E0] rounded-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {student.studentFirstName || student.firstName} {student.studentLastName || student.lastName}
                              </h4>
                              <p className="text-sm text-gray-500">
                                Öğrenci No: {student.studentNumber || 'Belirtilmemiş'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Bu veliye atanmış öğrenci bulunmuyor</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Veli bulunamadı</h3>
              <p className="text-sm text-gray-500">Veli detayları yüklenirken bir hata oluştu.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentDetailModal;
